import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';

export interface DateFilterRange {
  fromDate: string; // 'YYYY-MM-DD'
  toDate: string;   // 'YYYY-MM-DD'
  mode: string;     // matches the dropdown value
  /** True when thisWeek/previousWeek spans across two calendar months. */
  crossesMonths?: boolean;
}

/**
 * Reusable date-range filter — extracted from
 * `/nora/fee-management/report-collection`.
 *
 * Modes: today, yesterday, thisWeek, previousWeek, thisMonth, previousMonth,
 * specificMonth (paired month picker), thisQuarter, previousQuarter,
 * thisYear, previousYear, custom (paired date inputs).
 *
 * Quarter calculations align to the academic year when `academicYearStart`
 * and `academicYearEnd` are supplied; otherwise they fall back to calendar
 * quarters.
 *
 * Emits `(rangeChange)` on every effective change.
 */
@Component({
  selector: 'app-date-filter',
  templateUrl: './date-filter.component.html',
  styleUrls: ['./date-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DateFilterComponent implements OnInit, OnChanges {
  /** Optional academic-year window for academic-quarter math. */
  @Input() academicYearStart: Date | string | null = null;
  @Input() academicYearEnd: Date | string | null = null;

  /** Initial mode (default 'thisMonth'). */
  @Input() defaultMode = 'thisMonth';

  /** Hide the leading "Select Date Filter" empty option (default false). */
  @Input() hideEmptyOption = false;

  /** Compact label next to the dropdown. */
  @Input() label = 'Date Filter';

  /** Hide the field label entirely (matches sibling filters that don't show labels). */
  @Input() hideLabel = false;

  /** Width helper for the column wrapper. Defaults to bootstrap col-md-2. */
  @Input() colClass = 'col-md-2 spaceDivBottom mainAllSpace';

  /** Optional tinted backdrop on the popover (when used as standalone). */
  @Input() inline = true;

  @Output() rangeChange = new EventEmitter<DateFilterRange>();

  readonly options = [
    { value: '', label: 'Select Date Filter' },
    { value: 'academicYear', label: 'Academic Year' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'previousWeek', label: 'Previous Week' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'previousMonth', label: 'Previous Month' },
    { value: 'specificMonth', label: 'Specific Month' },
    { value: 'thisQuarter', label: 'This Quarter' },
    { value: 'previousQuarter', label: 'Previous Quarter' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'previousYear', label: 'Previous Year' },
    { value: 'custom', label: 'Custom' }
  ];

  mode = '';
  fromDate = '';
  toDate = '';
  selectedMonth = '';

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (!this.mode) this.mode = this.defaultMode || 'thisMonth';
    this.applyMode(this.mode, /*emit*/ true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If the academic-year inputs change after init, recompute the current
    // mode (especially relevant for thisQuarter / previousQuarter).
    if ((changes['academicYearStart'] || changes['academicYearEnd']) && this.mode) {
      if (this.mode !== 'custom' && this.mode !== 'specificMonth') {
        this.applyMode(this.mode, /*emit*/ true);
      }
    }
  }

  get visibleOptions() {
    return this.hideEmptyOption ? this.options.filter(o => o.value !== '') : this.options;
  }

  onModeChange(mode: string): void {
    this.mode = mode;
    if (mode === 'custom') {
      this.fromDate = '';
      this.toDate = '';
      this.emit();
      return;
    }
    if (mode === 'specificMonth') {
      // Wait for the month picker; clear current range.
      this.fromDate = '';
      this.toDate = '';
      this.emit();
      return;
    }
    this.applyMode(mode, /*emit*/ true);
  }

  onMonthPicked(): void {
    if (!this.selectedMonth) return;
    const [y, m] = this.selectedMonth.split('-').map(Number);
    if (!y || !m) return;
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0);
    this.fromDate = this.fmt(start);
    this.toDate = this.fmt(end);
    this.emit();
  }

  onCustomDateChange(): void {
    this.emit();
  }

  /** External callers can clear the filter (e.g. clear-filters button). */
  reset(mode: string = ''): void {
    this.mode = mode;
    this.selectedMonth = '';
    this.fromDate = '';
    this.toDate = '';
    if (mode) this.applyMode(mode, /*emit*/ true);
    else this.emit();
    this.cdr.markForCheck();
  }

  /**
   * Programmatically restore a previously-persisted date filter state.
   * Called by ReportPageLayoutComponent when re-hydrating saved report state.
   *
   * @param mode        The saved filter mode token (e.g. 'thisMonth', 'custom').
   * @param fromDate    Saved from-date (YYYY-MM-DD) — only used for 'custom' mode.
   * @param toDate      Saved to-date  (YYYY-MM-DD) — only used for 'custom' mode.
   */
  restoreState(mode: string, fromDate?: string, toDate?: string): void {
    if (!mode) return;
    this.mode = mode;
    if (mode === 'custom') {
      this.fromDate = fromDate ?? '';
      this.toDate = toDate ?? '';
      // Emit whatever dates are set (may be empty strings if none were saved).
      this.emit();
    } else if (mode === 'specificMonth' && fromDate) {
      // fromDate is stored as YYYY-MM-DD — extract YYYY-MM for the month picker.
      this.selectedMonth = fromDate.substring(0, 7);
      this.onMonthPicked();
    } else {
      this.applyMode(mode, /*emit*/ true);
    }
    this.cdr.markForCheck();
  }

  private applyMode(mode: string, emit: boolean): void {
    if (!mode) {
      this.fromDate = '';
      this.toDate = '';
      if (emit) this.emit();
      return;
    }

    const today = new Date();
    let from: Date | null = null;
    let to: Date = today;

    switch (mode) {
      case 'today':
        from = today;
        break;

      case 'yesterday': {
        const y = new Date(today);
        y.setDate(today.getDate() - 1);
        from = y;
        to = new Date(y);
        break;
      }

      case 'thisWeek': {
        const start = new Date(today);
        const dow = today.getDay();
        const diff = dow === 0 ? 6 : dow - 1; // Monday-as-week-start
        start.setDate(today.getDate() - diff);
        from = start;
        break;
      }

      case 'previousWeek': {
        const lastWeekEnd = new Date(today);
        const dow = today.getDay();
        const daysToLastSunday = dow === 0 ? 7 : dow;
        lastWeekEnd.setDate(today.getDate() - daysToLastSunday);
        const start = new Date(lastWeekEnd);
        start.setDate(lastWeekEnd.getDate() - 6);
        from = start;
        to = lastWeekEnd;
        break;
      }

      case 'thisMonth':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        break;

      case 'previousMonth':
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;

      case 'thisQuarter': {
        const q = this.getAcademicQuarterDates(today, 'current');
        if (q) { from = q.start; to = q.end; }
        else {
          const cq = Math.floor(today.getMonth() / 3);
          from = new Date(today.getFullYear(), cq * 3, 1);
        }
        break;
      }

      case 'previousQuarter': {
        const q = this.getAcademicQuarterDates(today, 'previous');
        if (q) { from = q.start; to = q.end; }
        else {
          const pq = Math.floor(today.getMonth() / 3) - 1;
          if (pq < 0) {
            from = new Date(today.getFullYear() - 1, 9, 1);
            to = new Date(today.getFullYear() - 1, 11, 31);
          } else {
            from = new Date(today.getFullYear(), pq * 3, 1);
            to = new Date(today.getFullYear(), pq * 3 + 3, 0);
          }
        }
        break;
      }

      case 'academicYear': {
        // Full academic year (start → end). Falls back to the calendar year-to-date if the
        // academic-year window hasn't loaded yet; ngOnChanges re-applies once it arrives.
        const ays = this.toDate0(this.academicYearStart);
        const aye = this.toDate0(this.academicYearEnd);
        if (ays && aye) { from = ays; to = aye; }
        else { from = new Date(today.getFullYear(), 0, 1); }
        break;
      }

      case 'thisYear':
        from = new Date(today.getFullYear(), 0, 1);
        break;

      case 'previousYear':
        from = new Date(today.getFullYear() - 1, 0, 1);
        to = new Date(today.getFullYear() - 1, 11, 31);
        break;

      case 'custom':
      case 'specificMonth':
        return;

      default:
        from = new Date(today.getFullYear(), today.getMonth(), 1);
    }

    if (from) {
      this.fromDate = this.fmt(from);
      this.toDate = this.fmt(to);
      if (emit) this.emit();
    }
  }

  private getAcademicQuarterDates(currentDate: Date, type: 'current' | 'previous'): { start: Date; end: Date } | null {
    const ays = this.toDate0(this.academicYearStart);
    const aye = this.toDate0(this.academicYearEnd);
    if (!ays || !aye) return null;

    const totalDays = Math.floor((aye.getTime() - ays.getTime()) / (1000 * 60 * 60 * 24));
    if (totalDays <= 0) return null;
    const qDays = Math.floor(totalDays / 4);

    const daysFromStart = Math.floor((currentDate.getTime() - ays.getTime()) / (1000 * 60 * 60 * 24));
    let cq = Math.max(0, Math.min(3, Math.floor(daysFromStart / qDays)));
    let target = type === 'current' ? cq : cq - 1;
    if (target < 0) return null;

    const start = new Date(ays);
    start.setDate(ays.getDate() + target * qDays);
    const end = new Date(ays);
    if (target === 3) end.setTime(aye.getTime());
    else end.setDate(ays.getDate() + (target + 1) * qDays - 1);
    return { start, end };
  }

  private toDate0(v: Date | string | null): Date | null {
    if (!v) return null;
    return v instanceof Date ? v : new Date(v);
  }

  private fmt(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private emit(): void {
    const crossesMonths = this.detectCrossMonth();
    this.rangeChange.emit({ fromDate: this.fromDate, toDate: this.toDate, mode: this.mode, crossesMonths });
  }

  private detectCrossMonth(): boolean {
    if (this.mode !== 'thisWeek' && this.mode !== 'previousWeek') return false;
    if (!this.fromDate || !this.toDate) return false;
    const [fy, fm] = this.fromDate.split('-').map(Number);
    const [ty, tm] = this.toDate.split('-').map(Number);
    return fy !== ty || fm !== tm;
  }
}
